/**
 * Scenario Templates for Transformer Condition Assessment
 * These scenarios provide pre-configured diagnostic prescriptions
 * based on common real-world situations
 */

export const SCENARIO_TEMPLATES = [
  {
    id: "new_transformer",
    name: "New Transformer Commissioning",
    shortDescription: "Baseline testing for newly installed transformer",
    icon: "sparkles",
    risk_level: "low",
    risk_color: "#22c55e", // green
    
    situation: {
      summary: "Brand new transformer has been installed and needs baseline measurements before energization. Establishing reference values for future comparison is critical for long-term condition monitoring.",
      bullet_points: [
        "Brand new transformer, just installed",
        "No operational history available",
        "Need to establish baseline measurements",
        "Verify manufacturer specifications"
      ]
    },
    
    sample_specs: {
      rated_power_mva: 25,
      voltage_hv_kv: 66,
      voltage_lv_kv: 11,
      year_manufactured: 2024,
      cooling_type: "ONAN",
      age_years: 0
    },
    
    operating_conditions: {
      avg_load_percent: 0,
      peak_load_percent: 0,
      ambient_temp_c: 30,
      status: "Pre-commissioning"
    },
    
    key_concerns: [
      "Manufacturing defects",
      "Transportation damage",
      "Installation errors",
      "Baseline documentation"
    ],
    
    prescribed_tests: {
      priority_1: [
        { 
          name: "Turns Ratio Test", 
          reason: "Verify winding integrity and tap positions match nameplate",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Insulation Resistance", 
          reason: "Establish baseline insulation condition before energization",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "SFRA (Sweep Frequency Response)", 
          reason: "Create mechanical fingerprint for future comparison",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Winding Resistance", 
          reason: "Verify connections and detect any loose joints",
          component: "Winding & Magnetic Circuit"
        }
      ],
      priority_2: [
        { 
          name: "Oil Quality Test", 
          reason: "Confirm oil meets specifications before service",
          component: "Main Tank & Oil"
        },
        { 
          name: "Power Factor Test", 
          reason: "Baseline bushing and insulation condition",
          component: "HV/LV/MV Bushings"
        }
      ]
    },
    
    prescribed_monitoring: {
      critical: [
        { 
          name: "Online Temperature Monitoring", 
          reason: "Track thermal behavior during initial loading",
          component: "Main Tank & Oil"
        }
      ],
      recommended: [
        { 
          name: "Load Monitoring", 
          reason: "Ensure loading within design parameters",
          component: "Winding & Magnetic Circuit"
        }
      ]
    },
    
    recommendation_summary: "Perform comprehensive baseline testing before energization. Document all results for future trending and comparison. Initial loading should be gradual with close thermal monitoring."
  },
  
  {
    id: "aged_transformer",
    name: "Aged Transformer (20+ Years)",
    shortDescription: "Assessment for transformer approaching end-of-life",
    icon: "clock",
    risk_level: "high",
    risk_color: "#ef4444", // red
    
    situation: {
      summary: "This transformer has been in service for over 20 years and is approaching its design end-of-life. Critical assessment is needed to determine remaining useful life and plan for replacement or refurbishment.",
      bullet_points: [
        "22-year-old power transformer",
        "Approaching design end-of-life (typically 25-35 years)",
        "Multiple repairs in operational history",
        "Unknown remaining useful life",
        "Strategic replacement planning required"
      ]
    },
    
    sample_specs: {
      rated_power_mva: 50,
      voltage_hv_kv: 132,
      voltage_lv_kv: 33,
      year_manufactured: 2003,
      cooling_type: "ONAN/ONAF",
      age_years: 22
    },
    
    operating_conditions: {
      avg_load_percent: 55,
      peak_load_percent: 75,
      ambient_temp_c: 35,
      status: "In Service - Under Review"
    },
    
    key_concerns: [
      "Paper insulation degradation",
      "Accumulated thermal aging",
      "Potential winding deformation",
      "Bushing deterioration",
      "Oil contamination buildup"
    ],
    
    prescribed_tests: {
      priority_1: [
        { 
          name: "DGA (Dissolved Gas Analysis)", 
          reason: "CRITICAL - Detects thermal and electrical faults, trending essential",
          component: "Main Tank & Oil"
        },
        { 
          name: "Furan Analysis", 
          reason: "Determines paper insulation degradation and estimates remaining life",
          component: "Main Tank & Oil"
        },
        { 
          name: "SFRA (Sweep Frequency Response)", 
          reason: "Detects winding movement or deformation from years of service",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Power Factor / Tan Delta", 
          reason: "Assesses overall insulation system condition",
          component: "Winding & Magnetic Circuit"
        }
      ],
      priority_2: [
        { 
          name: "Oil Quality & Moisture", 
          reason: "Check for contamination and moisture ingress over years",
          component: "Main Tank & Oil"
        },
        { 
          name: "Infrared Thermography", 
          reason: "Identify hot spots and connection issues",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Bushing Power Factor", 
          reason: "Bushings are critical failure point in aged transformers",
          component: "HV/LV/MV Bushings"
        }
      ]
    },
    
    prescribed_monitoring: {
      critical: [
        { 
          name: "Online DGA Monitor", 
          reason: "HIGH PRIORITY - Continuous fault gas trending for early warning",
          component: "Main Tank & Oil"
        },
        { 
          name: "Bushing Monitors", 
          reason: "Bushing failures are catastrophic - continuous monitoring essential",
          component: "HV/LV/MV Bushings"
        }
      ],
      recommended: [
        { 
          name: "Partial Discharge Sensors", 
          reason: "Early detection of insulation breakdown",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Winding Hot Spot Monitors", 
          reason: "Track thermal aging acceleration",
          component: "Winding & Magnetic Circuit"
        }
      ]
    },
    
    recommendation_summary: "Comprehensive condition assessment is critical. Consider 6-month DGA trending minimum before making replacement decisions. Online monitoring strongly recommended for remaining service life. Begin replacement planning if Furan analysis indicates DP < 400."
  },
  
  {
    id: "overloaded_transformer",
    name: "Continuously Overloaded (>60% Rated)",
    shortDescription: "Assessment for transformer under sustained high loading",
    icon: "zap",
    risk_level: "medium-high",
    risk_color: "#f97316", // orange
    
    situation: {
      summary: "This transformer operates continuously at high loading (>60% of rated capacity) with frequent peaks above 80%. Accelerated aging is expected, requiring enhanced monitoring and more frequent testing.",
      bullet_points: [
        "Running at 65% average load continuously",
        "Peak loads regularly reaching 85%",
        "Accelerated thermal aging expected",
        "12-year-old unit showing signs of stress",
        "Limited capacity for load growth"
      ]
    },
    
    sample_specs: {
      rated_power_mva: 40,
      voltage_hv_kv: 110,
      voltage_lv_kv: 22,
      year_manufactured: 2013,
      cooling_type: "ONAN/ONAF",
      age_years: 12
    },
    
    operating_conditions: {
      avg_load_percent: 65,
      peak_load_percent: 85,
      ambient_temp_c: 38,
      status: "In Service - High Utilization"
    },
    
    key_concerns: [
      "Accelerated insulation aging",
      "Hot spot temperature elevation",
      "Cooling system adequacy",
      "Oil degradation rate",
      "Load growth limitations"
    ],
    
    prescribed_tests: {
      priority_1: [
        { 
          name: "DGA (Dissolved Gas Analysis)", 
          reason: "Thermal stress indicators - check for overheating gases (ethylene, methane)",
          component: "Main Tank & Oil"
        },
        { 
          name: "Infrared Thermography", 
          reason: "Full thermal survey during peak load conditions",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Oil Quality Test", 
          reason: "Check oxidation rate - accelerated by high temperatures",
          component: "Main Tank & Oil"
        }
      ],
      priority_2: [
        { 
          name: "Cooling System Inspection", 
          reason: "Verify fans/pumps operating correctly under high load",
          component: "Cooling System"
        },
        { 
          name: "Furan Analysis", 
          reason: "Assess paper aging rate under thermal stress",
          component: "Main Tank & Oil"
        }
      ]
    },
    
    prescribed_monitoring: {
      critical: [
        { 
          name: "Continuous Thermal Monitoring", 
          reason: "Real-time hot spot tracking essential for overloaded units",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Load Profile Monitoring", 
          reason: "Track loading patterns and cumulative thermal stress",
          component: "Winding & Magnetic Circuit"
        }
      ],
      recommended: [
        { 
          name: "Online DGA", 
          reason: "Early detection of thermal faults from overloading",
          component: "Main Tank & Oil"
        },
        { 
          name: "Cooling Fan/Pump Monitoring", 
          reason: "Cooling system is critical - monitor for failures",
          component: "Cooling System"
        }
      ]
    },
    
    recommendation_summary: "Consider load management strategies to reduce average loading below 60%. Ensure cooling systems are optimized. Increase DGA testing frequency to quarterly. Evaluate if additional cooling can be retrofitted."
  },
  
  {
    id: "thermal_stress",
    name: "Thermal Stress (Daily 90°C+ Peaks)",
    shortDescription: "Assessment for transformer experiencing extreme temperatures",
    icon: "thermometer",
    risk_level: "high",
    risk_color: "#ef4444", // red
    
    situation: {
      summary: "This transformer experiences daily peak temperatures exceeding 90°C for 1-2 hours. Combined with high ambient temperatures, this creates significant thermal stress that accelerates insulation aging exponentially.",
      bullet_points: [
        "Daily peak temperatures exceeding 90°C",
        "High ambient environment (45°C average)",
        "1-2 hours of thermal stress daily",
        "Located in hot climate region",
        "Insulation life significantly reduced"
      ]
    },
    
    sample_specs: {
      rated_power_mva: 31.5,
      voltage_hv_kv: 132,
      voltage_lv_kv: 11,
      year_manufactured: 2015,
      cooling_type: "ONAN/ONAF",
      age_years: 10
    },
    
    operating_conditions: {
      avg_load_percent: 58,
      peak_load_percent: 80,
      ambient_temp_c: 45,
      peak_winding_temp_c: 92,
      status: "In Service - Thermal Concern"
    },
    
    key_concerns: [
      "Exponential paper aging above 90°C",
      "Oil oxidation acceleration",
      "Gasket and seal degradation",
      "Reduced expected lifespan",
      "Risk of thermal runaway"
    ],
    
    prescribed_tests: {
      priority_1: [
        { 
          name: "DGA (Dissolved Gas Analysis)", 
          reason: "Focus on thermal fault gases - ethylene, methane trends critical",
          component: "Main Tank & Oil"
        },
        { 
          name: "Furan Analysis", 
          reason: "CRITICAL - Paper degradation rate under thermal stress",
          component: "Main Tank & Oil"
        },
        { 
          name: "Oil Dielectric Strength", 
          reason: "Thermal stress degrades oil - verify breakdown voltage",
          component: "Main Tank & Oil"
        },
        { 
          name: "Infrared Thermography", 
          reason: "Full survey during peak temperature period",
          component: "Winding & Magnetic Circuit"
        }
      ],
      priority_2: [
        { 
          name: "Degree of Polymerization", 
          reason: "Direct paper condition measurement if accessible",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Oil Moisture Content", 
          reason: "High temps can release moisture from insulation",
          component: "Main Tank & Oil"
        }
      ]
    },
    
    prescribed_monitoring: {
      critical: [
        { 
          name: "Winding Hot Spot Monitor", 
          reason: "CRITICAL - Real-time tracking of peak temperatures",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Online DGA Monitor", 
          reason: "Thermal fault detection as it develops",
          component: "Main Tank & Oil"
        }
      ],
      recommended: [
        { 
          name: "Top Oil Temperature Trending", 
          reason: "Correlate with ambient and load patterns",
          component: "Main Tank & Oil"
        },
        { 
          name: "Ambient Temperature Monitoring", 
          reason: "Environmental correlation for load management",
          component: "Cooling System"
        }
      ]
    },
    
    recommendation_summary: "URGENT: Implement load management to keep hot spot below 90°C. Consider enhanced cooling retrofit. Furan trending every 6 months minimum. If paper DP approaching 400, begin replacement planning. Each hour above 90°C significantly reduces remaining life."
  },
  
  {
    id: "post_fault",
    name: "Post-Fault Assessment",
    shortDescription: "Emergency assessment after through-fault or protection operation",
    icon: "alert-triangle",
    risk_level: "critical",
    risk_color: "#dc2626", // dark red
    
    situation: {
      summary: "This transformer experienced a through-fault event and protection operated successfully. Before re-energization, comprehensive assessment is required to verify no damage occurred and the unit is safe to return to service.",
      bullet_points: [
        "Through-fault event occurred",
        "Protection relay operated correctly",
        "Transformer currently de-energized",
        "Unknown internal condition",
        "Must assess before re-energization"
      ]
    },
    
    sample_specs: {
      rated_power_mva: 63,
      voltage_hv_kv: 220,
      voltage_lv_kv: 66,
      year_manufactured: 2010,
      cooling_type: "ONAF",
      age_years: 15
    },
    
    operating_conditions: {
      avg_load_percent: 0,
      peak_load_percent: 0,
      fault_type: "External short circuit",
      fault_duration_ms: 150,
      status: "De-energized - Under Assessment"
    },
    
    key_concerns: [
      "Winding displacement from fault forces",
      "Connection damage from fault current",
      "Core damage from magnetic forces",
      "Bushing flashover possibility",
      "Internal arc damage"
    ],
    
    prescribed_tests: {
      priority_1: [
        { 
          name: "SFRA (Sweep Frequency Response)", 
          reason: "CRITICAL - Compare with baseline to detect winding movement",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Turns Ratio Test", 
          reason: "Verify no winding damage or turn-to-turn faults",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Winding Resistance", 
          reason: "Check for connection loosening from fault forces",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "DGA (Dissolved Gas Analysis)", 
          reason: "Detect any fault gases generated during event",
          component: "Main Tank & Oil"
        },
        { 
          name: "Insulation Resistance", 
          reason: "Verify insulation integrity post-fault",
          component: "Winding & Magnetic Circuit"
        }
      ],
      priority_2: [
        { 
          name: "Bushing Power Factor", 
          reason: "Check for bushing damage from fault current",
          component: "HV/LV/MV Bushings"
        },
        { 
          name: "Core Ground Test", 
          reason: "Verify core insulation integrity",
          component: "Winding & Magnetic Circuit"
        }
      ]
    },
    
    prescribed_monitoring: {
      critical: [
        { 
          name: "Enhanced DGA Monitoring", 
          reason: "Close monitoring after return to service for delayed failures",
          component: "Main Tank & Oil"
        }
      ],
      recommended: [
        { 
          name: "Vibration Monitoring", 
          reason: "Detect core/winding looseness from fault forces",
          component: "Winding & Magnetic Circuit"
        },
        { 
          name: "Partial Discharge Monitoring", 
          reason: "Detect insulation damage that may develop into failure",
          component: "Winding & Magnetic Circuit"
        }
      ]
    },
    
    recommendation_summary: "DO NOT RE-ENERGIZE without completing Priority 1 tests. Compare SFRA results with baseline - any significant deviation requires internal inspection. If no baseline exists, proceed with caution and implement enhanced monitoring. DGA sample within 24 hours of fault, then daily for first week after re-energization."
  }
];

export const getRiskLevelBadge = (level) => {
  const badges = {
    low: { text: "LOW RISK", color: "bg-green-100 text-green-800 border-green-300" },
    medium: { text: "MEDIUM RISK", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
    "medium-high": { text: "MEDIUM-HIGH RISK", color: "bg-orange-100 text-orange-800 border-orange-300" },
    high: { text: "HIGH RISK", color: "bg-red-100 text-red-800 border-red-300" },
    critical: { text: "CRITICAL", color: "bg-red-200 text-red-900 border-red-400" }
  };
  return badges[level] || badges.medium;
};

export const getScenarioById = (id) => {
  return SCENARIO_TEMPLATES.find(s => s.id === id);
};
